import React from "react";
import { Button, ButtonVariant, CardinalLocation, Tooltip, TruncatedText } from "@hex-insights/core";
import {
	FieldIf,
	FormState,
	FormTooltipButton,
	FormTooltipButtonButtonProps,
	FormTooltipButtonTooltipProps,
	MultiSelectField,
	RadioButtonsInput,
	RadioField,
	SelectField,
} from "@hex-insights/forms";
import { ClassYearFormat } from "../../../Format";
import { AttendanceStatusSelectField, CampusSelectField, HomeRoomSectionSelectField } from "../../../Forms";
import { ClassYearUtilities } from "../../../Utilities";
import * as StudentFilterFormValues from "../form-values";

const filterTooltipSideOrder: CardinalLocation[] = ["bottom", "top", "right", "left"];

type BaseFieldProps<K extends keyof StudentFilterFormValues.FilterFormValues> = {
	formState: FormState<Pick<StudentFilterFormValues.FilterFormValues, K>>;
};

export function StatusEQTooltipButton({ formState }: BaseFieldProps<"statusEQ">) {
	return (
		<FormTooltipButton formState={formState} sideOrder={filterTooltipSideOrder} autoFocus>
			{StatusEQTooltipButton.Tooltip}
			{StatusEQTooltipButton.Button}
		</FormTooltipButton>
	);
}

StatusEQTooltipButton.Tooltip = function ({
	formState,
}: FormTooltipButtonTooltipProps<Pick<StudentFilterFormValues.FilterFormValues, "statusEQ">>) {
	return (
		<Tooltip isInteractable>
			<Tooltip.Body>
				<StatusEQ formState={formState} />
			</Tooltip.Body>
		</Tooltip>
	);
};

StatusEQTooltipButton.Button = function ({
	formState,
	onClick,
}: FormTooltipButtonButtonProps<Pick<StudentFilterFormValues.FilterFormValues, "statusEQ">>) {
	const statusEQ = formState.formValues.statusEQ;

	return (
		<Button variant={statusEQ ? "primary" : "tertiary"} onClick={onClick}>
			{statusEQ ? formState.formFormatFunctions.statusEQ(statusEQ) : "Status"}
		</Button>
	);
};

const statusEQOptions = [
	{ value: null, label: "All" },
	{ value: "Enrolled" },
	{ value: "Future" },
	{ value: "Former" },
	{ value: "Not Enrolled" },
];

export function StatusEQ({ formState }: BaseFieldProps<"statusEQ">) {
	return (
		<RadioField
			formState={formState}
			name="statusEQ"
			label="Status"
			options={statusEQOptions}
			Input={RadioButtonsInput}
			blankValue={null}
			noOptional
		/>
	);
}

export function CampusIDsInTooltipButton({ formState }: BaseFieldProps<"campusIDsIn">) {
	return (
		<FormTooltipButton formState={formState} sideOrder={filterTooltipSideOrder} autoFocus>
			{CampusIDsInTooltipButton.Tooltip}
			{CampusIDsInTooltipButton.Button}
		</FormTooltipButton>
	);
}

CampusIDsInTooltipButton.Tooltip = function ({
	formState,
}: FormTooltipButtonTooltipProps<Pick<StudentFilterFormValues.FilterFormValues, "campusIDsIn">>) {
	return (
		<Tooltip isInteractable>
			<Tooltip.Body>
				<CampusIDsIn formState={formState} />
			</Tooltip.Body>
		</Tooltip>
	);
};

CampusIDsInTooltipButton.Button = function ({
	formState,
	onClick,
}: FormTooltipButtonButtonProps<Pick<StudentFilterFormValues.FilterFormValues, "campusIDsIn">>) {
	const campusIDsIn = formState.formValues.campusIDsIn;

	return (
		<Button variant={campusIDsIn.length > 0 ? "primary" : "tertiary"} onClick={onClick}>
			<TruncatedText style={{ maxWidth: "10rem" }}>
				{campusIDsIn.length > 0 ? `${formState.formFormatFunctions.campusIDsIn(campusIDsIn)}` : "Campus"}
			</TruncatedText>
		</Button>
	);
};

export function CampusIDsIn({ formState }: BaseFieldProps<"campusIDsIn">) {
	return <CampusSelectField.MultiSelect formState={formState} name="campusIDsIn" label="Campuses" noOptional />;
}

export function GraduationYearInTooltipButton({ formState }: BaseFieldProps<"graduationYearIn">) {
	return (
		<FormTooltipButton formState={formState} sideOrder={filterTooltipSideOrder} autoFocus>
			{GraduationYearInTooltipButton.Tooltip}
			{GraduationYearInTooltipButton.Button}
		</FormTooltipButton>
	);
}

GraduationYearInTooltipButton.Tooltip = function ({
	formState,
}: FormTooltipButtonTooltipProps<Pick<StudentFilterFormValues.FilterFormValues, "graduationYearIn">>) {
	return (
		<Tooltip isInteractable>
			<Tooltip.Body>
				<GraduationYearIn formState={formState} />
			</Tooltip.Body>
		</Tooltip>
	);
};

GraduationYearInTooltipButton.Button = function ({
	formState,
	onClick,
}: FormTooltipButtonButtonProps<Pick<StudentFilterFormValues.FilterFormValues, "graduationYearIn">>) {
	const graduationYearIn = formState.formValues.graduationYearIn;

	return (
		<Button variant={graduationYearIn.length > 0 ? "primary" : "tertiary"} onClick={onClick}>
			<TruncatedText style={{ maxWidth: "10rem" }}>
				{graduationYearIn.length > 0
					? `${formState.formFormatFunctions.graduationYearIn(graduationYearIn)}`
					: "Class Year"}
			</TruncatedText>
		</Button>
	);
};

export function GraduationYearIn({ formState }: BaseFieldProps<"graduationYearIn">) {
	const { loading, data: classYearsData } = ClassYearUtilities.useClassYearsMap();
	const options = React.useMemo(() => {
		if (!classYearsData) {
			return [];
		}

		return classYearsData.classYearConnection.edges.map(({ node }) => ({
			value: node.graduationYear,
			label: ClassYearFormat.name(node),
		}));
	}, [classYearsData]);

	return (
		<MultiSelectField
			formState={formState}
			name="graduationYearIn"
			label="Class Years"
			isLoading={loading}
			options={options}
			noOptional
		/>
	);
}

export function GraduationYearEQTooltipButton({ formState }: BaseFieldProps<"graduationYearEQ">) {
	return (
		<FormTooltipButton formState={formState} sideOrder={filterTooltipSideOrder} autoFocus>
			{GraduationYearEQTooltipButton.Tooltip}
			{GraduationYearEQTooltipButton.Button}
		</FormTooltipButton>
	);
}

GraduationYearEQTooltipButton.Tooltip = function ({
	formState,
}: FormTooltipButtonTooltipProps<Pick<StudentFilterFormValues.FilterFormValues, "graduationYearEQ">>) {
	return (
		<Tooltip isInteractable>
			<Tooltip.Body>
				<GraduationYearEQ formState={formState} />
			</Tooltip.Body>
		</Tooltip>
	);
};

GraduationYearEQTooltipButton.Button = function ({
	formState,
	onClick,
}: FormTooltipButtonButtonProps<Pick<StudentFilterFormValues.FilterFormValues, "graduationYearEQ">>) {
	const graduationYearEQ = formState.formValues.graduationYearEQ;

	return (
		<Button variant={graduationYearEQ !== null ? "primary" : "tertiary"} onClick={onClick}>
			<TruncatedText style={{ maxWidth: "10rem" }}>
				{graduationYearEQ !== null
					? `${formState.formFormatFunctions.graduationYearEQ(graduationYearEQ)}`
					: "Class Year"}
			</TruncatedText>
		</Button>
	);
};

export function GraduationYearEQ({ formState }: BaseFieldProps<"graduationYearEQ">) {
	const { loading, data: classYearsData } = ClassYearUtilities.useClassYearsMap();
	const options = React.useMemo(() => {
		if (!classYearsData) {
			return [];
		}

		return classYearsData.classYearConnection.edges.map(({ node }) => ({
			value: node.graduationYear,
			label: ClassYearFormat.name(node),
		}));
	}, [classYearsData]);

	return (
		<SelectField
			formState={formState}
			name="graduationYearEQ"
			label="Class Year"
			isLoading={loading}
			options={options}
			blankValue={null}
			noOptional
		/>
	);
}

export function HomeRoomSectionIDInTooltipButton({ formState }: BaseFieldProps<"homeRoomSectionIDIn">) {
	return (
		<FormTooltipButton formState={formState} sideOrder={filterTooltipSideOrder} autoFocus>
			{HomeRoomSectionIDInTooltipButton.Tooltip}
			{HomeRoomSectionIDInTooltipButton.Button}
		</FormTooltipButton>
	);
}

HomeRoomSectionIDInTooltipButton.Tooltip = function ({
	formState,
}: FormTooltipButtonTooltipProps<Pick<StudentFilterFormValues.FilterFormValues, "homeRoomSectionIDIn">>) {
	return (
		<Tooltip isInteractable>
			<Tooltip.Body>
				<HomeRoomSectionIDIn formState={formState} />
			</Tooltip.Body>
		</Tooltip>
	);
};

HomeRoomSectionIDInTooltipButton.Button = function ({
	formState,
	onClick,
}: FormTooltipButtonButtonProps<Pick<StudentFilterFormValues.FilterFormValues, "homeRoomSectionIDIn">>) {
	const homeRoomSectionIDIn = formState.formValues.homeRoomSectionIDIn;

	return (
		<Button variant={homeRoomSectionIDIn.length > 0 ? "primary" : "tertiary"} onClick={onClick}>
			<TruncatedText style={{ maxWidth: "10rem" }}>
				{homeRoomSectionIDIn.length > 0
					? `${formState.formFormatFunctions.homeRoomSectionIDIn(homeRoomSectionIDIn)}`
					: "Home Room Section"}
			</TruncatedText>
		</Button>
	);
};

export function HomeRoomSectionIDIn({ formState }: BaseFieldProps<"homeRoomSectionIDIn">) {
	return (
		<HomeRoomSectionSelectField.MultiSelect
			formState={formState}
			name="homeRoomSectionIDIn"
			label="Home Room Sections"
			noOptional
		/>
	);
}

export function AttendanceTodayTooltipButton({
	formState,
}: BaseFieldProps<"hasAttendanceToday" | "attendanceStatusTodayIDIn">) {
	return (
		<FormTooltipButton formState={formState} sideOrder={filterTooltipSideOrder} autoFocus>
			{AttendanceTodayTooltipButton.Tooltip}
			{AttendanceTodayTooltipButton.Button}
		</FormTooltipButton>
	);
}

AttendanceTodayTooltipButton.Tooltip = function ({
	formState,
}: FormTooltipButtonTooltipProps<
	Pick<StudentFilterFormValues.FilterFormValues, "hasAttendanceToday" | "attendanceStatusTodayIDIn">
>) {
	return (
		<Tooltip isInteractable>
			<Tooltip.Body>
				<HasAttendanceToday formState={formState} />
				<FieldIf
					formState={formState}
					name="attendanceStatusTodayIDIn"
					condition={formState.formValues.hasAttendanceToday !== false}
				>
					<AttendanceStatusTodayIDIn formState={formState} />
				</FieldIf>
			</Tooltip.Body>
		</Tooltip>
	);
};

AttendanceTodayTooltipButton.Button = function ({
	formState,
	onClick,
}: FormTooltipButtonButtonProps<
	Pick<StudentFilterFormValues.FilterFormValues, "hasAttendanceToday" | "attendanceStatusTodayIDIn">
>) {
	const attendanceStatusTodayIDIn = formState.formValues.attendanceStatusTodayIDIn;

	let text = "Attendance Status";
	let variant: ButtonVariant = "tertiary";
	if (formState.formValues.hasAttendanceToday === false) {
		text = "Not Taken";
		variant = "primary";
	} else if (attendanceStatusTodayIDIn.length > 0) {
		text = formState.formFormatFunctions.attendanceStatusTodayIDIn(attendanceStatusTodayIDIn);
		variant = "primary";
	} else if (formState.formValues.hasAttendanceToday === true) {
		text = "Taken";
		variant = "primary";
	}

	return (
		<Button variant={variant} onClick={onClick}>
			<TruncatedText style={{ maxWidth: "10rem" }}>{text}</TruncatedText>
		</Button>
	);
};

export function AttendanceStatusTodayIDIn({ formState }: BaseFieldProps<"attendanceStatusTodayIDIn">) {
	return (
		<AttendanceStatusSelectField.MultiSelect
			formState={formState}
			name="attendanceStatusTodayIDIn"
			label="Attendance Status Today"
			noOptional
		/>
	);
}

const hasAttendanceTodayOptions = [
	{ value: null, label: "All" },
	{ value: true, label: "Taken" },
	{ value: false, label: "Not Taken" },
];

export function HasAttendanceToday({ formState }: BaseFieldProps<"hasAttendanceToday">) {
	return (
		<RadioField
			formState={formState}
			name="hasAttendanceToday"
			label="Attendance Today"
			noOptional
			options={hasAttendanceTodayOptions}
			Input={RadioButtonsInput}
		/>
	);
}

export function ReEnrollmentStatusEQTooltipButton({ formState }: BaseFieldProps<"reEnrollmentStatusEQ">) {
	return (
		<FormTooltipButton formState={formState} sideOrder={filterTooltipSideOrder} autoFocus>
			{ReEnrollmentStatusEQTooltipButton.Tooltip}
			{ReEnrollmentStatusEQTooltipButton.Button}
		</FormTooltipButton>
	);
}

ReEnrollmentStatusEQTooltipButton.Tooltip = function ({
	formState,
}: FormTooltipButtonTooltipProps<Pick<StudentFilterFormValues.FilterFormValues, "reEnrollmentStatusEQ">>) {
	return (
		<Tooltip isInteractable>
			<Tooltip.Body>
				<ReEnrollmentStatusEQ formState={formState} />
			</Tooltip.Body>
		</Tooltip>
	);
};

ReEnrollmentStatusEQTooltipButton.Button = function ({
	formState,
	onClick,
}: FormTooltipButtonButtonProps<Pick<StudentFilterFormValues.FilterFormValues, "reEnrollmentStatusEQ">>) {
	const reEnrollmentStatusEQ = formState.formValues.reEnrollmentStatusEQ;

	return (
		<Button variant={reEnrollmentStatusEQ ? "primary" : "tertiary"} onClick={onClick}>
			{reEnrollmentStatusEQ ? formState.formFormatFunctions.reEnrollmentStatusEQ(reEnrollmentStatusEQ) : "Status"}
		</Button>
	);
};

const reEnrollmentStatusEQOptions = [
	{ value: null, label: "All" },
	{ value: "Re-Enrolling", label: "Re-Enrolling" }, // to avoid conversion to TitleCase
	{ value: "Withdrawing" },
	{ value: "No Response" },
];

export function ReEnrollmentStatusEQ({ formState }: BaseFieldProps<"reEnrollmentStatusEQ">) {
	return (
		<RadioField
			formState={formState}
			name="reEnrollmentStatusEQ"
			label="Status"
			options={reEnrollmentStatusEQOptions}
			Input={RadioButtonsInput}
			blankValue={null}
			noOptional
		/>
	);
}

export function ReEnrollmentMealPlanTooltipButton({ formState }: BaseFieldProps<"reEnrollmentMealPlan">) {
	return (
		<FormTooltipButton formState={formState} sideOrder={filterTooltipSideOrder} autoFocus>
			{ReEnrollmentMealPlanTooltipButton.Tooltip}
			{ReEnrollmentMealPlanTooltipButton.Button}
		</FormTooltipButton>
	);
}

ReEnrollmentMealPlanTooltipButton.Tooltip = function ({
	formState,
}: FormTooltipButtonTooltipProps<Pick<StudentFilterFormValues.FilterFormValues, "reEnrollmentMealPlan">>) {
	return (
		<Tooltip isInteractable>
			<Tooltip.Body>
				<ReEnrollmentMealPlan formState={formState} />
			</Tooltip.Body>
		</Tooltip>
	);
};

ReEnrollmentMealPlanTooltipButton.Button = function ({
	formState,
	onClick,
}: FormTooltipButtonButtonProps<Pick<StudentFilterFormValues.FilterFormValues, "reEnrollmentMealPlan">>) {
	const reEnrollmentMealPlan = formState.formValues.reEnrollmentMealPlan;

	return (
		<Button variant={reEnrollmentMealPlan !== null ? "primary" : "tertiary"} onClick={onClick}>
			{reEnrollmentMealPlan !== null
				? formState.formFormatFunctions.reEnrollmentMealPlan(reEnrollmentMealPlan)
				: "Meal Plan"}
		</Button>
	);
};

const reEnrollmentMealPlanOptions = [
	{ value: null, label: "All" },
	{ value: true, label: "Interested" },
	{ value: false, label: "Not Interested" },
];

export function ReEnrollmentMealPlan({ formState }: BaseFieldProps<"reEnrollmentMealPlan">) {
	return (
		<RadioField
			formState={formState}
			name="reEnrollmentMealPlan"
			label="Meal Plan"
			options={reEnrollmentMealPlanOptions}
			Input={RadioButtonsInput}
			blankValue={null}
			noOptional
		/>
	);
}

export function ReEnrollmentContractStatusEQTooltipButton({
	formState,
}: BaseFieldProps<"reEnrollmentContractStatusEQ">) {
	return (
		<FormTooltipButton formState={formState} sideOrder={filterTooltipSideOrder} autoFocus>
			{ReEnrollmentContractStatusEQTooltipButton.Tooltip}
			{ReEnrollmentContractStatusEQTooltipButton.Button}
		</FormTooltipButton>
	);
}

ReEnrollmentContractStatusEQTooltipButton.Tooltip = function ({
	formState,
}: FormTooltipButtonTooltipProps<Pick<StudentFilterFormValues.FilterFormValues, "reEnrollmentContractStatusEQ">>) {
	return (
		<Tooltip isInteractable>
			<Tooltip.Body>
				<ReEnrollmentContractStatusEQ formState={formState} />
			</Tooltip.Body>
		</Tooltip>
	);
};

ReEnrollmentContractStatusEQTooltipButton.Button = function ({
	formState,
	onClick,
}: FormTooltipButtonButtonProps<Pick<StudentFilterFormValues.FilterFormValues, "reEnrollmentContractStatusEQ">>) {
	const reEnrollmentContractStatusEQ = formState.formValues.reEnrollmentContractStatusEQ;

	return (
		<Button variant={reEnrollmentContractStatusEQ ? "primary" : "tertiary"} onClick={onClick}>
			{reEnrollmentContractStatusEQ
				? formState.formFormatFunctions.reEnrollmentContractStatusEQ(reEnrollmentContractStatusEQ)
				: "Status"}
		</Button>
	);
};

const reEnrollmentContractStatusEQOptions = [
	{ value: null, label: "All" },
	{ value: "Incomplete" },
	{ value: "Ready for Review" },
	{ value: "Approved" },
	{ value: "Signed & Uploaded" },
];

export function ReEnrollmentContractStatusEQ({ formState }: BaseFieldProps<"reEnrollmentContractStatusEQ">) {
	return (
		<RadioField
			formState={formState}
			name="reEnrollmentContractStatusEQ"
			label="Status"
			options={reEnrollmentContractStatusEQOptions}
			Input={RadioButtonsInput}
			blankValue={null}
			noOptional
		/>
	);
}
